"use client";

import { Suspense, useState } from "react";
import { LoginForm, type LegalDoc } from "@/components/LoginForm";
import { PRIVACY, TERMS, type LegalBlock } from "@/lib/legal";

function Feature({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3.5 text-[17px] text-white/70">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#4ade80"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="shrink-0"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
      {label}
    </div>
  );
}

function Brand() {
  return (
    <div className="flex h-full flex-col justify-between p-12 xl:p-16">
      <div className="text-[18px] font-bold tracking-tight text-white/55" style={{ letterSpacing: "-0.03em" }}>
        Dost <span className="text-white">Partner</span> Console
      </div>
      <div>
        <h1 className="text-[48px] font-semibold leading-[1.02] tracking-tight text-white xl:text-[58px]">
          Ship agents
          <br />
          on Dost.
        </h1>
        <p className="mt-6 max-w-[380px] text-[16px] leading-relaxed text-white/45">
          The Dost Console platform for building, verifying, and shipping on the Dost agent network.
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <Feature label="Verified human network" />
        <Feature label="0% commission" />
        <Feature label="End-to-end encrypted" />
      </div>
    </div>
  );
}

function Block({ b }: { b: LegalBlock }) {
  if (b.t === "h") return <div className="pt-3 text-[15px] font-medium text-white/90">{b.text}</div>;
  if (b.t === "sub") return <div className="pt-1 text-[13.5px] font-medium text-white/70">{b.text}</div>;
  if (b.t === "ul")
    return (
      <ul className="ml-4 list-disc space-y-1.5 text-[13.5px] leading-relaxed text-white/45 marker:text-white/25">
        {b.items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    );
  return <p className="text-[13.5px] leading-relaxed text-white/45">{b.text}</p>;
}

function LegalReader({
  doc,
  onBack,
  onSwitch,
}: {
  doc: LegalDoc;
  onBack: () => void;
  onSwitch: (d: LegalDoc) => void;
}) {
  const c = doc === "terms" ? TERMS : PRIVACY;
  const other: LegalDoc = doc === "terms" ? "privacy" : "terms";
  return (
    <div className="flex h-full flex-col p-11 xl:p-14">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 self-start text-[13.5px] text-white/55 transition-colors hover:text-white"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M19 12H5" />
          <path d="m12 19-7-7 7-7" />
        </svg>
        Back
      </button>

      <div className="mt-8 shrink-0">
        <h2 className="text-[26px] font-semibold tracking-tight text-white">{c.title}</h2>
        <div className="mt-1.5 text-[12.5px] uppercase tracking-wide text-white/35">{c.effective}</div>
      </div>

      <div key={doc} className="legal-body mt-6 min-h-0 flex-1 space-y-3 overflow-y-auto pr-4">
        {c.blocks.map((b, i) => (
          <Block key={i} b={b} />
        ))}
      </div>

      <div className="mt-5 shrink-0 border-t border-white/[0.07] pt-4 text-[13px] text-white/40">
        Also see{" "}
        <button
          type="button"
          onClick={() => onSwitch(other)}
          className="text-white/70 underline underline-offset-2 transition-colors hover:text-white"
        >
          {other === "terms" ? "Terms & Conditions" : "Privacy Policy"}
        </button>
        .
      </div>
    </div>
  );
}

export function LoginSplit() {
  const [legal, setLegal] = useState<LegalDoc | null>(null);

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,42%)_1fr]">
      <aside className="relative hidden overflow-hidden border-r border-white/[0.06] bg-[#111112] lg:block">
        <div
          className={`absolute inset-0 transition-all duration-300 ease-out ${
            legal ? "pointer-events-none -translate-x-4 opacity-0" : "translate-x-0 opacity-100"
          }`}
        >
          <Brand />
        </div>
        <div
          className={`absolute inset-0 transition-all duration-300 ease-out ${
            legal ? "translate-x-0 opacity-100" : "pointer-events-none translate-x-5 opacity-0"
          }`}
        >
          {legal && <LegalReader doc={legal} onBack={() => setLegal(null)} onSwitch={setLegal} />}
        </div>
      </aside>

      <main className="flex items-center justify-center bg-[#09090b] px-6 py-16">
        <div className="w-full max-w-[400px]">
          <div
            className="mb-9 text-[18px] font-bold tracking-tight text-white/55 lg:hidden"
            style={{ letterSpacing: "-0.03em" }}
          >
            Dost <span className="text-white">Partner</span> Console
          </div>
          <Suspense fallback={<div className="text-sm text-white/40">Loading…</div>}>
            <LoginForm onOpenLegal={setLegal} />
          </Suspense>
        </div>
      </main>
    </div>
  );
}
